import React, { useState, useCallback, useEffect } from 'react';
import { ListFilter } from 'lucide-react';

import SearchBox from '../ui-components/SearchBox';
import SelectBox from '../ui-components/SelectBox';
import { useI18n } from '../i18n/index.jsx';

const SearchFilters = ({
  searchQuery,
  onSearch,
  availableFilters,
  activeFilters,
  onFilterChange,
  filteredItemsCount = 0,
  // Pre-translated noun pair for the results banner; defaults to "session(s)".
  itemNouns = null,
  showSearch = true,
  searchPlaceholder = null,
  handleClearFilters,
}) => {
  const { t } = useI18n();
  const nouns = itemNouns || { one: t('item_session'), other: t('items_sessions') };
  const effectiveSearchPlaceholder = searchPlaceholder || t('search_placeholder_speakers_sessions');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  // SB-8455: the mobile sheet is deferred-apply. Selections made inside it
  // accumulate here and only reach onFilterChange when "Apply filters" is
  // pressed; × / overlay discard them. null = sheet closed / no draft.
  const [draftFilters, setDraftFilters] = useState(null);

  const hasActiveFilters = searchQuery.trim() || Object.values(activeFilters).some(arr => arr.length > 0);

  const handleFilterChange = (filterType, selectedValues) => {
    onFilterChange(filterType, selectedValues);
  };

  const openMobileFilters = () => {
    // Snapshot the committed filters so the sheet edits a draft copy.
    setDraftFilters({ ...activeFilters });
    setShowMobileFilters(true);
  };

  // × / overlay: dismiss and discard the pending selection.
  const cancelMobileFilters = () => {
    setShowMobileFilters(false);
    setDraftFilters(null);
  };

  // SB-8456: the sheet declares role="dialog" aria-modal="true", so Escape has
  // to dismiss it. Bound on the document rather than the sheet because nothing
  // moves focus into the sheet on open — a key pressed anywhere in the widget
  // has to reach this. KeyboardEvents are composed, so they cross the widget's
  // Shadow DOM boundary. Escape discards, matching × and the overlay.
  useEffect(() => {
    if (!showMobileFilters) return undefined;
    const onKeyDown = event => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      cancelMobileFilters();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [showMobileFilters]);

  // "Apply filters": commit every filter type whose draft differs.
  const applyMobileFilters = () => {
    const draft = draftFilters || {};
    const keys = new Set([...Object.keys(draft), ...Object.keys(activeFilters)]);
    keys.forEach(filterType => {
      const next = draft[filterType] || [];
      const current = activeFilters[filterType] || [];
      const unchanged =
        next.length === current.length && next.every(value => current.includes(value));
      if (!unchanged) {
        onFilterChange(filterType, next);
      }
    });
    setShowMobileFilters(false);
    setDraftFilters(null);
  };

  const renderFilter = useCallback(
    (filterType, filterConfig, { values, onChange } = {}) => {
      const selectedValues = values ?? (activeFilters[filterType] || []);
      const count = selectedValues.length;
      return (
        <SelectBox
          key={filterType}
          label={filterConfig.name}
          options={filterConfig.options.map(opt => ({ value: opt.id, label: opt.label }))}
          value={selectedValues}
          onChange={onChange ?? (newValues => handleFilterChange(filterType, newValues))}
          multiple={filterConfig.allowMultiple}
          showCount={count > 0}
          count={count}
        />
      );
    },
    [activeFilters, handleFilterChange]
  );

  // Mobile sheet: bind SelectBoxes to the draft, not the live filters.
  const renderDraftFilter = (filterType, filterConfig) =>
    renderFilter(filterType, filterConfig, {
      values: (draftFilters && draftFilters[filterType]) || [],
      onChange: newValues =>
        setDraftFilters(prev => ({ ...(prev || {}), [filterType]: newValues })),
    });

  return (
    <>
      <div className="search-filters-enhanced">
        {showSearch && <SearchBox placeholder={effectiveSearchPlaceholder} value={searchQuery} onChange={onSearch} />}

        {/* SB-8456: a bare div could not be reached by keyboard at all, so the
            Escape fix below had no way of being exercised. Same role/tabIndex/
            onKeyDown shape the overlay already uses. */}
        <div
          className="search-filters-enhanced__show-filters"
          onClick={openMobileFilters}
          role="button"
          tabIndex={0}
          aria-haspopup="dialog"
          aria-expanded={showMobileFilters}
          onKeyDown={event => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              openMobileFilters();
            }
          }}
        >
          <ListFilter />
          {t('filters')}
        </div>

        <div className="search-filters-enhanced__filters-content">
          {Object.entries(availableFilters).map(([filterType, filterConfig]) => renderFilter(filterType, filterConfig))}
        </div>

        {/* Mobile Filters Modal */}
        {showMobileFilters && (
          <div className="search-filters-enhanced__mobile-modal" style={{ insetInlineEnd: 0 }}>
            <div
              className="search-filters-enhanced__mobile-menu"
              role="dialog"
              aria-modal="true"
              aria-labelledby="embed-mobile-filters-title"
            >
              <div className="search-filters-enhanced__mobile-header">
                <h3 id="embed-mobile-filters-title">{t('filters')}</h3>
                <button
                  type="button"
                  onClick={cancelMobileFilters}
                  aria-label={t('close_filters')}
                >
                  ×
                </button>
              </div>
              <div className="search-filters-enhanced__mobile-body">
                {Object.entries(availableFilters).map(([filterType, filterConfig]) =>
                  renderDraftFilter(filterType, filterConfig)
                )}
              </div>
              <div className="search-filters-enhanced__mobile-footer">
                <button
                  type="button"
                  className="search-filters-enhanced__mobile-apply"
                  onClick={applyMobileFilters}
                >
                  {t('apply_filters')}
                </button>
              </div>
            </div>
            <div
              className="search-filters-enhanced__mobile-overlay"
              onClick={cancelMobileFilters}
              role="button"
              tabIndex={0}
              aria-label={t('close_filters')}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  cancelMobileFilters();
                }
              }}
            />
          </div>
        )}
      </div>

      {/* Enhanced Filter Results Banner */}
      {hasActiveFilters && (
        <div
          className={`filter-banner-enhanced ${filteredItemsCount === 0 ? 'filter-banner-enhanced--no-results' : ''}`}
        >
          <div className="filter-banner-enhanced__content">
            <ListFilter size={16} className="filter-banner-enhanced__icon" />
            <span className="filter-banner-enhanced__text">
              {filteredItemsCount === 0
                ? searchQuery
                  ? t('no_items_with_title', { items: nouns.other, query: searchQuery })
                  : t('showing_zero_match_filters', { items: nouns.other })
                : searchQuery
                  ? t.plural('showing_with_title', filteredItemsCount, {
                      items: filteredItemsCount === 1 ? nouns.one : nouns.other,
                      query: searchQuery,
                    })
                  : t.plural('showing_match_filters', filteredItemsCount, {
                      items: filteredItemsCount === 1 ? nouns.one : nouns.other,
                    })}
            </span>

            {/* Active Filter Tags */}
            <div className="filter-banner-enhanced__tags">
              {Object.entries(activeFilters).map(([filterType, selectedValues]) => {
                const filterConfig = availableFilters[filterType];
                if (!filterConfig) return null;

                const validValues = selectedValues.filter(value =>
                  filterConfig.options.some(opt => opt.id === value)
                );
                if (validValues.length === 0) return null;

                return (
                  <div key={filterType} className="filter-banner-enhanced__tag">
                    <span className="filter-banner-enhanced__tag-name">{filterConfig.name}:</span>
                    <span className="sb-dot-list">
                      {validValues.map(value => {
                        const option = filterConfig.options.find(opt => opt.id === value);
                        return (
                          <span key={value} className="sb-dot-list__item filter-banner-enhanced__tag-value">
                            {option.label}
                            <button
                              type="button"
                              className="filter-banner-enhanced__tag-close"
                              onClick={() => {
                                const newValues = selectedValues.filter(v => v !== value);
                                handleFilterChange(filterType, newValues);
                              }}
                            >
                              ×
                            </button>
                          </span>
                        );
                      })}
                    </span>
                  </div>
                );
              })}
            </div>

            <button className="filter-banner-enhanced__clear" onClick={handleClearFilters}>
              {t('clear_filters')}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default SearchFilters;
